<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content mb-3">
        <!-- Compact Header -->
        <div class="page-header mb-3">
          <div>
            <h4 class="mb-1">Tableau de bord</h4>
            <p class="text-muted small mb-0">Vue d'ensemble de votre activité</p>
          </div>
          <button class="btn btn-sm btn-outline-primary" :disabled="isLoading" @click="loadDashboard">
            <vue-feather type="refresh-cw" size="14" class="me-1"></vue-feather>
            Actualiser
          </button>
        </div>

        <div v-if="isLoading" class="dashboard-loading">
          <div class="spinner-border text-primary dashboard-spinner" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
        </div>

        <template v-else>
          <!-- Summary Cards -->
          <div class="row g-3 mb-3">
            <div v-for="(card, index) in summaryCards" :key="card.label" class="col-xl-3 col-sm-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in"
                :style="`animation-delay: ${index * 0.1}s`">
                <div class="card-body p-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="icon-wrapper bg-primary-subtle rounded p-2 icon-pulse">
                        <vue-feather :type="card.icon" size="15" class="text-primary"></vue-feather>
                      </div>
                    </div>
                    <div class="flex-grow-1 min-w-0">
                      <p class="text-muted small mb-1 text-truncate">{{ card.label }}</p>
                      <h5 class="mb-0 text-truncate counter-animate">{{ card.displayValue }}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Abonnement Card -->
          <div class="row g-3 mb-3">
            <div class="col-12">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.4s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                    <div class="min-w-0 flex-grow-1">
                      <h6 class="mb-1">Abonnement</h6>
                      <small class="text-muted text-truncate d-block" v-if="abonnementPeriodText">{{
                        abonnementPeriodText }}</small>
                    </div>
                    <span v-if="hasAbonnementDetails" class="badge flex-shrink-0" :class="abonnementStatusBadgeClass">
                      {{ abonnementStatusLabel }}
                    </span>
                  </div>

                  <template v-if="hasAbonnementDetails">
                    <div class="row g-3 mb-3">
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Formule</small>
                        <strong class="small d-block text-truncate">{{ abonnement.nom_abonnement || "—" }}</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Période</small>
                        <strong class="small d-block text-truncate">{{ abonnementDatesRange }}</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Temps restant</small>
                        <strong class="small d-block text-truncate">
                          {{
                            abonnement.dates?.jours_restants !== null &&
                              abonnement.dates?.jours_restants !== undefined
                              ? formatDaysRemaining(abonnement.dates?.jours_restants)
                              : "—"
                          }}
                        </strong>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-lg-6">
                        <div class="d-flex justify-content-between align-items-center mb-1 gap-2">
                          <small class="fw-semibold text-truncate">Utilisateurs</small>
                          <small class="text-muted flex-shrink-0">
                            {{ abonnement.utilisateurs?.actuels || 0 }} /
                            {{ abonnement.utilisateurs?.limite ?? "∞" }}
                          </small>
                        </div>
                        <div class="progress" style="height: 6px;">
                          <div class="progress-bar bg-primary" :style="{ width: `${abonnementUsersPercent}%` }"></div>
                        </div>
                      </div>
                      <div class="col-lg-6">
                        <div class="d-flex justify-content-between align-items-center mb-1 gap-2">
                          <small class="fw-semibold text-truncate">Administrateurs</small>
                          <small class="text-muted flex-shrink-0">
                            {{ abonnementAdministrateurs?.actuels || 0 }} /
                            {{ abonnementAdministrateurs?.limite ?? "∞" }}
                          </small>
                        </div>
                        <div class="progress" style="height: 6px;">
                          <div class="progress-bar bg-info" :style="{ width: `${abonnementAdminsPercent}%` }"></div>
                        </div>
                      </div>
                    </div>

                    <div v-if="(abonnement.alertes || []).length" class="mt-3 pt-3 border-top">
                      <ul class="list-unstyled mb-0">
                        <li v-for="(alerte, index) in abonnement.alertes || []" :key="index" :class="[
                          'd-flex align-items-start small mb-1',
                          alerte.type === 'danger' ? 'text-danger' : 'text-warning',
                        ]">
                          <vue-feather type="alert-triangle" size="14" class="me-2 flex-shrink-0 mt-1"></vue-feather>
                          <span class="text-break">{{ alerte.message }}</span>
                        </li>
                      </ul>
                    </div>
                  </template>
                  <p v-else class="text-muted small mb-0">
                    {{ abonnement?.message || "Aucun abonnement actif." }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Revenue Chart (Moved Up) -->
          <div class="row g-3 mb-3">
            <div class="col-12">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.5s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 class="mb-0">Évolution du CA (12 mois)</h6>
                    <small v-if="revenueChartSeries.length" class="text-muted text-truncate">
                      Total: {{formatCurrency(revenueChartSeries[0].data.reduce((sum, value) => sum + (Number(value) ||
                      0), 0))
                      }}
                    </small>
                  </div>
                  <ApexChart v-if="revenueChartSeries.length" type="area" height="250" :options="revenueChartOptions"
                    :series="revenueChartSeries" />
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- KPIs & Client Breakdown with Donuts -->
          <div class="row g-3 mb-3">
            <div class="col-lg-8">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.6s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Indicateurs clés</h6>
                  <div class="row g-2">
                    <div v-for="kpi in kpiCards" :key="kpi.label" class="col-md-6 col-sm-6">
                      <div class="p-2 bg-light rounded kpi-card">
                        <small class="text-muted d-block mb-1 text-truncate">{{ kpi.label }}</small>
                        <strong class="small d-block text-truncate">{{ kpi.displayValue }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.7s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Répartition clients</h6>
                  <div v-if="clientBreakdown.length">
                    <ApexChart type="donut" height="200" :options="clientDonutOptions" :series="clientDonutSeries" />
                    <div class="mt-2 d-flex flex-column gap-1">
                      <div v-for="(item, idx) in clientBreakdown" :key="item.label"
                        class="d-flex justify-content-between align-items-center small legend-item">
                        <div class="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
                          <span class="legend-dot flex-shrink-0"
                            :style="{ backgroundColor: clientDonutOptions.colors[idx] }"></span>
                          <span class="text-muted text-truncate">{{ item.label }}</span>
                        </div>
                        <span class="badge bg-primary-subtle text-primary flex-shrink-0">{{ item.value }}</span>
                      </div>
                    </div>
                  </div>
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Operations & Financial Summary -->
          <div class="row g-3 mb-3">
            <div class="col-lg-8">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.8s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 class="mb-0">Tableau opérationnel</h6>
                    <small class="text-muted" v-if="operationsSnapshot && operationsSnapshot.generated_at">
                      {{ formatDate(operationsSnapshot.generated_at) }}
                    </small>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Indicateur</th>
                          <th class="text-end small">Total</th>
                          <th class="text-end small">Mois</th>
                          <th class="text-end small">Semaine</th>
                          <th class="text-end small">Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!operationsMetrics.length">
                          <td colspan="5" class="text-center text-muted py-3 small">
                            Aucune donnée disponible.
                          </td>
                        </tr>
                        <tr v-for="metric in operationsMetrics" :key="metric.key">
                          <td class="small text-truncate" style="max-width: 150px;">{{ metric.label }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.total) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.this_month) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.this_week) }}</td>
                          <td class="text-end small text-truncate" style="max-width: 100px;">
                            {{ formatMetricAmount(metric.amount_total) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 0.9s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Performance financière</h6>
                  <template v-if="financialSummary">
                    <div v-if="financialDonutSeries.length">
                      <ApexChart type="donut" height="180" :options="financialDonutOptions"
                        :series="financialDonutSeries" />
                    </div>
                    <div class="mt-3">
                      <div class="mb-2 p-2 bg-light rounded financial-card">
                        <div class="d-flex justify-content-between mb-1 gap-2">
                          <small class="text-muted text-truncate">Mois en cours</small>
                          <strong class="small text-truncate">{{ formatCurrency(financialSummary.current_month?.amount)
                            }}</strong>
                        </div>
                        <small class="text-muted d-block mb-1">
                          {{ formatNumberOrDash(financialSummary.current_month?.count) }} facture(s)
                        </small>
                        <small :class="trendClass(financialSummary.month_variation_percent)">
                          {{ formatTrendPercent(financialSummary.month_variation_percent) }}
                        </small>
                      </div>
                      <div class="d-flex flex-column gap-2">
                        <div class="d-flex justify-content-between align-items-center gap-2">
                          <small class="text-muted text-truncate">Facture moy.</small>
                          <strong class="small text-truncate">{{ formatCurrency(financialSummary.average_invoice_amount)
                            }}</strong>
                        </div>
                      </div>
                    </div>
                  </template>
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Clients & Stock Alerts -->
          <div class="row g-3 mb-3">
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Top clients</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Client</th>
                          <th class="text-end small">Factures</th>
                          <th class="text-end small">Total</th>
                          <th class="text-end small">Attente</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!topClientsRows.length">
                          <td colspan="4" class="text-center text-muted py-3 small">
                            Aucune donnée disponible.
                          </td>
                        </tr>
                        <tr v-for="client in topClientsRows" :key="client.client_id">
                          <td class="small text-truncate" style="max-width: 150px;">{{ client.client_name }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(client.factures_count) }}</td>
                          <td class="text-end small text-truncate" style="max-width: 100px;">{{
                            formatCurrency(client.total_amount) }}</td>
                          <td class="text-end small text-truncate" style="max-width: 100px;">
                            <span :class="client.outstanding_amount > 0 ? 'text-danger fw-semibold' : ''">
                              {{ formatCurrency(client.outstanding_amount) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.1s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 class="mb-0">Alertes stock</h6>
                    <div v-if="stockAlertsSummary" class="d-flex gap-2 flex-wrap">
                      <span class="badge bg-danger-subtle text-danger small alert-badge">{{
                        formatNumberOrDash(stockAlertsSummary.critical) }}</span>
                      <span class="badge bg-warning-subtle text-warning small">{{
                        formatNumberOrDash(stockAlertsSummary.low)
                        }}</span>
                      <span class="badge bg-info-subtle text-info small">{{
                        formatNumberOrDash(stockAlertsSummary.below_optimal)
                        }}</span>
                    </div>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Produit</th>
                          <th class="text-end small">Stock</th>
                          <th class="text-end small">Seuil</th>
                          <th class="small">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!stockAlertsItems.length">
                          <td colspan="4" class="text-center text-muted py-3 small">
                            Aucune alerte.
                          </td>
                        </tr>
                        <tr v-for="item in stockAlertsItems" :key="item.product_id">
                          <td class="small text-truncate" style="max-width: 120px;">{{ item.name }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(item.current_stock) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(item.threshold) }}</td>
                          <td>
                            <span class="badge small" :class="stockStatusBadgeClass(item.status)">
                              {{ stockStatusLabel(item.status) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="row g-3">
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.2s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Dernières factures</h6>
                  <div v-if="recentFactures.length === 0" class="text-muted small">
                    Aucune facture récente.
                  </div>
                  <div v-else class="d-flex flex-column gap-2">
                    <div v-for="facture in recentFactures" :key="facture.id"
                      class="d-flex justify-content-between align-items-center p-2 bg-light rounded gap-2 activity-item">
                      <div class="min-w-0 flex-grow-1">
                        <strong class="small d-block text-truncate">#{{ facture.numero_facture || facture.id }}</strong>
                        <small class="text-muted text-truncate d-block">
                          {{ facture.commande?.client?.nom || "Client inconnu" }} • {{ formatDate(facture.created_at) }}
                        </small>
                      </div>
                      <strong class="small flex-shrink-0">{{ formatCurrency(facture.montant_total) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.3s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Nouveaux clients</h6>
                  <div v-if="recentClients.length === 0" class="text-muted small">
                    Aucun client récent.
                  </div>
                  <div v-else class="d-flex flex-column gap-2">
                    <div v-for="client in recentClients" :key="client.id"
                      class="d-flex justify-content-between align-items-center p-2 bg-light rounded gap-2 activity-item">
                      <div class="min-w-0 flex-grow-1">
                        <strong class="small d-block text-truncate">{{ client.nom }} {{ client.prenom }}</strong>
                        <small class="text-muted text-truncate d-block">{{ client.email || "Email non renseigné"
                          }}</small>
                      </div>
                      <small class="text-muted flex-shrink-0">{{ formatDate(client.created_at) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDashboardSummary,
  getDashboardKPIs,
  getDashboardClientStats,
  getDashboardFactureStats,
  getDashboardRecentActivity,
  getDashboardAbonnementStats,
  getDashboardOperations,
  getDashboardFinancials,
  getDashboardTopClients,
  getDashboardStockAlerts,
} from "@/services/dashboardService";
import { toast } from "vue3-toastify";

export default {
  name: "PosDashboard",
  data() {
    return {
      isLoading: false,
      summary: null,
      kpis: null,
      clientStats: null,
      factureStats: null,
      abonnement: null,
      operationsSnapshot: null,
      financialTrends: null,
      topClients: null,
      stockAlerts: null,
      activity: {
        dernieres_factures: [],
        derniers_clients: [],
      },
    };
  },
  computed: {
    summaryCards() {
      const s = this.summary || {};
      return [
        { label: "Clients", value: s.nombre_clients || 0, displayValue: this.formatNumber(s.nombre_clients || 0), icon: "users" },
        { label: "Factures", value: s.nombre_factures || 0, displayValue: this.formatNumber(s.nombre_factures || 0), icon: "file-text" },
        { label: "Montant facturé", value: s.total_factures || 0, displayValue: this.formatCurrency(s.total_factures || 0), icon: "trending-up" },
        { label: "Taux de paiement", value: s.taux_paiement || 0, displayValue: this.formatPercent(s.taux_paiement || 0), icon: "percent" },
      ];
    },
    kpiCards() {
      const k = this.kpis || {};
      return [
        { label: "CA mensuel", displayValue: this.formatCurrency(k.ca_mensuel || 0) },
        { label: "Croissance mensuelle", displayValue: this.formatPercent(k.croissance_mensuelle || 0) },
        { label: "Nouveaux clients", displayValue: this.formatNumber(k.nouveaux_clients_mois || 0) },
        { label: "Nouveaux devis", displayValue: this.formatNumber(k.nouveaux_devis_mois || 0) },
        { label: "Devis en attente", displayValue: this.formatNumber(k.devis_en_attente || 0) },
        { label: "Montant impayé", displayValue: this.formatCurrency(k.montant_impaye || 0) },
      ];
    },
    clientBreakdown() {
      const stats = this.clientStats || {};
      const categories = [
        { key: "clients_actifs", label: "Clients" },
        { key: "clients_fournisseurs", label: "Fournisseurs" },
        { key: "clients_prospects", label: "Prospects" },
      ];

      return categories
        .map(({ key, label }) => {
          const value = stats?.[key];
          return typeof value === "number" && value > 0 ? { label, value } : null;
        })
        .filter(Boolean);
    },
    clientDonutSeries() {
      return this.clientBreakdown.map(item => item.value);
    },
    clientDonutOptions() {
      const baseColors = ['#2E86DE', '#10AC84', '#FF6B6B', '#FFA502', '#A29BFE', '#6C5CE7'];
      const colors = this.clientBreakdown.map((_, idx) => baseColors[idx % baseColors.length]);
      const totalValue = this.clientBreakdown.reduce((sum, item) => sum + item.value, 0);

      return {
        chart: { type: 'donut' },
        labels: this.clientBreakdown.map(item => item.label),
        colors,
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '14px',
                  formatter: () => this.formatNumber(totalValue)
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatNumber(val)
          }
        }
      };
    },
    financialDonutSeries() {
      const f = this.financialSummary;
      if (!f) return [];
      return [
        f.paid_amount || 0,
        f.partial_amount || 0,
        f.unpaid_amount || 0
      ].filter(v => v > 0);
    },
    financialDonutOptions() {
      return {
        chart: { type: 'donut' },
        labels: ['Payé', 'Partiel', 'Impayé'],
        colors: ['#10AC84', '#FFA502', '#EE5A6F'],
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '12px',
                  formatter: () => {
                    const total = this.financialDonutSeries.reduce((sum, val) => sum + val, 0);
                    return this.formatCurrency(total).replace(' MAD', '');
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatCurrency(val)
          }
        }
      };
    },
    operationsMetrics() {
      const metrics = this.operationsSnapshot?.metrics || [];
      return metrics.map((metric) => ({
        key: metric.key,
        label: metric.label,
        total: metric.total ?? null,
        this_month: metric.this_month ?? null,
        this_week: metric.this_week ?? null,
        amount_total: metric.amount_total ?? null,
        amount_month: metric.amount_month ?? null,
        amount_week: metric.amount_week ?? null,
        paid_amount: metric.paid_amount ?? null,
        outstanding_amount: metric.outstanding_amount ?? null,
        extra: metric.extra ?? null,
      }));
    },
    financialSummary() {
      return this.financialTrends?.data || null;
    },
    topClientsRows() {
      if (Array.isArray(this.topClients?.data)) return this.topClients.data;
      if (Array.isArray(this.topClients)) return this.topClients;
      return [];
    },
    stockAlertsSummary() {
      return this.stockAlerts?.summary || null;
    },
    stockAlertsItems() {
      return Array.isArray(this.stockAlerts?.items) ? this.stockAlerts.items : [];
    },
    hasAbonnementDetails() {
      if (!this.abonnement) return false;
      const a = this.abonnement;
      return !!(a.nom_abonnement || a.statut || typeof a.abonnement_actif === "boolean" ||
        (a.dates && (a.dates.debut || a.dates.fin)) ||
        (a.utilisateurs && (a.utilisateurs.actuels !== undefined || a.utilisateurs.limite !== undefined)) ||
        (Array.isArray(a.alertes) && a.alertes.length));
    },
    abonnementStatusLabel() {
      const status = this.abonnement?.statut;
      switch (status) {
        case "actif": return "Actif";
        case "à_venir":
        case "a_venir": return "À venir";
        case "expiré":
        case "expire": return "Expiré";
        default: return this.abonnement?.abonnement_actif ? "Actif" : "Non défini";
      }
    },
    abonnementStatusBadgeClass() {
      const status = this.abonnement?.statut;
      if (status === "actif") return "bg-success-subtle text-success";
      if (status === "à_venir" || status === "a_venir") return "bg-warning-subtle text-warning";
      if (status === "expiré" || status === "expire") return "bg-danger-subtle text-danger";
      return "bg-secondary-subtle text-muted";
    },
    abonnementPeriodText() {
      const dates = this.abonnement?.dates;
      if (!dates) return null;
      const start = dates.debut ? this.formatDate(dates.debut) : null;
      const end = dates.fin ? this.formatDate(dates.fin) : null;
      if (start && end) return `Du ${start} au ${end}`;
      if (start) return `À partir du ${start}`;
      if (end) return `Jusqu'au ${end}`;
      return null;
    },
    abonnementDatesRange() {
      const dates = this.abonnement?.dates;
      if (!dates) return "—";
      const start = dates.debut ? this.formatDate(dates.debut) : "—";
      const end = dates.fin ? this.formatDate(dates.fin) : "—";
      return `${start} → ${end}`;
    },
    abonnementUsersPercent() {
      const limit = Number(this.abonnement?.utilisateurs?.limite ?? 0);
      const current = Number(this.abonnement?.utilisateurs?.actuels ?? 0);
      if (limit <= 0) return current > 0 ? 100 : 0;
      return Math.max(0, Math.min(Math.round((current / limit) * 100), 100));
    },
    abonnementAdministrateurs() {
      if (this.abonnement?.administrateurs) return this.abonnement.administrateurs;
      return {
        actuels: this.abonnement?.administrateurs_actuels ?? this.abonnement?.admin_actuels ?? 0,
        limite: this.abonnement?.administrateurs_limite ?? this.abonnement?.admin_limite ?? null,
      };
    },
    abonnementAdminsPercent() {
      const admins = this.abonnementAdministrateurs || {};
      const limit = Number(admins.limite ?? 0);
      const current = Number(admins.actuels ?? 0);
      if (limit <= 0) return current > 0 ? 100 : 0;
      return Math.max(0, Math.min(Math.round((current / limit) * 100), 100));
    },
    revenueChartSeries() {
      const entries = (this.factureStats?.ca_par_mois || []).map((item) => ({
        mois: item.mois,
        montant: Number(item.montant) || 0,
      }));
      if (!entries.length) return [];
      return [{ name: "Chiffre d'affaires", data: entries.map((item) => item.montant) }];
    },
    revenueChartOptions() {
      const entries = this.factureStats?.ca_par_mois || [];
      const categories = entries.map((item) => this.formatMonth(item.mois));
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#2E86DE"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { categories, labels: { style: { colors: "#6B7280", fontSize: "11px" } } },
        yaxis: {
          labels: {
            style: { fontSize: "11px" },
            formatter: (val) => {
              if (val === null || val === undefined) return "";
              return this.formatCurrency(val).replace(/\u00a0MAD$/, "");
            },
          },
        },
        tooltip: { y: { formatter: (val) => this.formatCurrency(val) } },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.3,
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0.1,
          },
        },
        markers: { size: 3, strokeWidth: 2, hover: { size: 5 } },
        grid: { strokeDashArray: 4, borderColor: "#E5E7EB" },
      };
    },
    recentFactures() {
      return this.activity?.dernieres_factures || [];
    },
    recentClients() {
      return this.activity?.derniers_clients || [];
    },
  },
  methods: {
    async loadDashboard() {
      this.isLoading = true;
      const requests = [
        ["summary", getDashboardSummary],
        ["kpis", getDashboardKPIs],
        ["clientStats", getDashboardClientStats],
        ["factureStats", getDashboardFactureStats],
        ["activity", getDashboardRecentActivity],
        ["abonnement", getDashboardAbonnementStats],
        ["operationsSnapshot", getDashboardOperations],
        ["financialTrends", getDashboardFinancials],
        ["topClients", getDashboardTopClients],
        ["stockAlerts", getDashboardStockAlerts],
      ];

      const errorSet = new Set();
      try {
        const results = await Promise.allSettled(requests.map(([, fn]) => fn()));
        results.forEach((result, index) => {
          const [key] = requests[index];
          if (result.status === "fulfilled") {
            const payload = result.value;
            if (payload && typeof payload === "object" && payload.success === false && payload.message) {
              errorSet.add(payload.message);
            }
            this[key] = payload;
          } else {
            const reason = result.reason;
            const apiMessage =
              reason?.response?.data?.message ||
              reason?.response?.data?.error ||
              reason?.message;
            errorSet.add(apiMessage || `Erreur lors du chargement de ${this.humanizeKey(key)}`);
            console.error(`Dashboard - ${key} failed`, reason);
          }
        });
      } finally {
        this.isLoading = false;
        if (errorSet.size) {
          const messages = Array.from(errorSet);
          toast.error(messages.length === 1 ? messages[0] : `Erreur de chargement du tableau de bord.\n${messages.join("\n")}`);
        }
      }
    },
    humanizeKey(key) {
      const mapping = {
        summary: "la synthèse",
        kpis: "les indicateurs",
        clientStats: "les statistiques clients",
        factureStats: "les statistiques factures",
        activity: "l'activité récente",
        abonnement: "l'abonnement",
        operationsSnapshot: "le tableau opérationnel",
        financialTrends: "les performances financières",
        topClients: "les meilleurs clients",
        stockAlerts: "les alertes stock",
      };
      return mapping[key] || key;
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "0,00\u00a0MAD";
      try {
        return new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "MAD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value));
      } catch (e) {
        return `${Number(value).toFixed(2)} MAD`;
      }
    },
    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(value || 0);
    },
    formatPercent(value) {
      return `${(Number(value) || 0).toFixed(2)}\u00a0%`;
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Intl.DateTimeFormat("fr-FR", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }).format(new Date(value));
      } catch (e) {
        return value;
      }
    },
    formatMonth(value) {
      if (!value) return "—";
      const [year, month] = value.split("-");
      try {
        return new Intl.DateTimeFormat("fr-FR", {
          month: "short",
          year: "numeric",
        }).format(new Date(Number(year), Number(month) - 1));
      } catch (e) {
        return value;
      }
    },
    formatNumberOrDash(value) {
      if (value === null || value === undefined) return "—";
      return this.formatNumber(value);
    },
    formatTrendPercent(value) {
      if (value === null || value === undefined) return "—";
      const number = Number(value);
      const sign = number > 0 ? "+" : "";
      return `${sign}${number.toFixed(2)} %`;
    },
    trendClass(value) {
      if (value === null || value === undefined) return "text-muted";
      if (value > 0) return "text-success";
      if (value < 0) return "text-danger";
      return "text-muted";
    },
    formatMetricAmount(value) {
      if (value === null || value === undefined) return "—";
      return this.formatCurrency(value);
    },
    formatDaysRemaining(value) {
      if (value === null || value === undefined) return "durée inconnue";
      const days = Number(value);
      if (Number.isNaN(days)) return "durée inconnue";
      if (days > 0) return `${days} jour${days > 1 ? "s" : ""}`;
      if (days === 0) return "Aujourd'hui";
      const abs = Math.abs(days);
      return `Expiré (${abs}j)`;
    },
    stockStatusLabel(status) {
      switch (status) {
        case "out": return "Critique";
        case "low": return "Faible";
        case "below-optimal": return "Sous opt.";
        default: return "OK";
      }
    },
    stockStatusBadgeClass(status) {
      switch (status) {
        case "out": return "bg-danger";
        case "low": return "bg-warning text-dark";
        case "below-optimal": return "bg-info text-dark";
        default: return "bg-success";
      }
    },
  },
  mounted() {
    this.loadDashboard();
  },
};
</script>

<style scoped>
/* Loading state */
.dashboard-loading {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-spinner {
  width: 3.5rem;
  height: 3.5rem;
  border-width: 0.35rem;
}

/* Fade-in animation for cards */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-fade-in {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

/* Hover effect for cards */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

/* Icon pulse animation */
@keyframes iconPulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

.icon-pulse {
  animation: iconPulse 2s ease-in-out infinite;
  transition: all 0.3s ease;
}

.card-hover:hover .icon-pulse {
  animation: none;
  transform: scale(1.1) rotate(5deg);
}

/* Counter animation */
.counter-animate {
  transition: all 0.3s ease;
}

/* Progress bar animation */
.progress-bar {
  transition: width 1s ease-in-out;
  animation: progressFill 1.5s ease-in-out;
}

@keyframes progressFill {
  from {
    width: 0;
  }
}

/* KPI card hover */
.kpi-card {
  transition: all 0.3s ease;
}

.kpi-card:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Financial card hover */
.financial-card {
  transition: all 0.3s ease;
}

.financial-card:hover {
  background-color: #e8f5e9 !important;
  transform: scale(1.02);
}

/* Legend item hover */
.legend-item {
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.legend-item:hover {
  background-color: rgba(46, 134, 222, 0.05);
}

/* Activity item animation */
.activity-item {
  transition: all 0.3s ease;
}

.activity-item:hover {
  background-color: #fff3cd !important;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Table row hover */
.table-hover tbody tr {
  transition: all 0.2s ease;
}

.table-hover tbody tr:hover {
  background-color: rgba(46, 134, 222, 0.05);
  transform: scale(1.01);
}

/* Badge pulse for alerts */
@keyframes badgePulse {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
  }
}

.alert-badge {
  animation: badgePulse 2s infinite;
}

/* Smooth loading animation */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Chart container fade-in */
.apexcharts-canvas {
  animation: fadeIn 0.8s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Button hover effect */
.btn {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 300px;
  height: 300px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.progress {
  background-color: #e9ecef;
  overflow: hidden;
}

.card {
  transition: box-shadow 0.2s ease;
  border-radius: 12px;
  overflow: hidden;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  transition: all 0.3s ease;
}

.badge:hover {
  transform: scale(1.05);
}

.bg-primary-subtle {
  background-color: rgba(46, 134, 222, 0.1) !important;
}

.bg-light {
  color: #152D4B !important;

}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1) !important;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1) !important;
}

.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  transition: all 0.3s ease;
}

.legend-dot:hover {
  transform: scale(1.5);
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-break {
  word-wrap: break-word;
  word-break: break-word;
}

.table td,
.table th {
  vertical-align: middle;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem !important;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .card-hover:hover {
    transform: none;
  }

  .icon-pulse {
    animation: none;
  }
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Page wrapper fade in */
.page-wrapper {
  animation: fadeIn 0.5s ease-in;
}
</style>
